import { useQueryClient } from '@tanstack/react-query'
import { useSet } from '@uidotdev/usehooks'
import StyledLoader from 'components/Elements/StyledLoader'
import { DataContext } from 'contexts/DataContextContainer'
import moment from 'moment'
import { useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import axiosClient, { updateBook } from 'services/axios-client'
import {
  useBookAuthorQuery,
  useBookQuery,
  useBookRoleQuery,
  useBookTagsQuery,
} from 'store/queries'
import GuideHeader from './components/GuideHeader'
import { useFormSteps } from './hooks'
import AddPeople from './steps/AddPeople'
import AddPrompts from './steps/AddPrompts'
import AuthorDetails from './steps/AuthorDetails'
import CompleteSetup from './steps/CompleteSetup'
import GuideIntro from './steps/GuideIntro'
import ProjectInfo from './steps/ProjectInfo'
import SetupGuideContainer from './style'

function SetupGuide() {
  const { bookId } = useParams()
  const queryClient = useQueryClient()
  const { lang } = useContext(DataContext)

  const [step, nextStep, prevStep] = useFormSteps()
  const tagIdsSet = useSet()

  const [projectInfoFormData, setProjectInfoFormData] = useState({ title: '' })
  const [authorFormData, setAuthorFormData] = useState(null)
  const [currentAuthorData, setCurrentAuthorData] = useState(null)
  const [invites, setInvites] = useState([])
  const [projectInfoUpdateComplete, setProjectInfoUpdateComplete] =
    useState(false)
  const [authorUpdateComplete, setAuthorUpdateComplete] = useState(false)
  const [invitesUpdateComplete, setInvitesUpdateComplete] = useState(false)
  const [promptsUpdateComplete, setPromptsUpdateComplete] = useState(false)

  const toggleTag = (tagId) => {
    if (tagIdsSet.has(tagId)) {
      tagIdsSet.delete(tagId)
    } else {
      tagIdsSet.add(tagId)
    }
  }
  const selectedTagIds = Object.fromEntries(tagIdsSet.entries())

  const { data: book } = useBookQuery(bookId)

  const { isSuccess: isBookTagsQuerySuccess, data: existingTags } =
    useBookTagsQuery(bookId)

  const { isSuccess: isAuthorQuerySuccess, data: bookWithAuthor } =
    useBookAuthorQuery(bookId)

  const { data: role } = useBookRoleQuery(bookId)

  const purchaserIsntAuthor =
    bookWithAuthor?.purchaser_user_id !== bookWithAuthor?.authorUser?.id

  // Init project info form state.
  useEffect(() => {
    // Initialize form one time from query result.
    // Force user to change placeholder title.
    // TODO: Think of a nicer way of doing this.
    if (book?.id && book?.title !== 'Edit Your Book Title') {
      const { title } = book

      setProjectInfoFormData({
        title,
      })
    }
  }, [book])

  // Init book author form state.
  useEffect(() => {
    if (bookWithAuthor && isAuthorQuerySuccess && authorFormData === null) {
      const { authorUser } = bookWithAuthor

      const startDate = bookWithAuthor.start_datetime
        ? moment.utc(bookWithAuthor.start_datetime)
        : moment.utc()

      setCurrentAuthorData({
        firstName: authorUser?.first_name,
        lastName: authorUser?.last_name,
        startDate,
      })

      if (purchaserIsntAuthor) {
        setAuthorFormData({
          firstName: authorUser?.first_name,
          lastName: authorUser?.last_name,
          email: authorUser?.email,
          emailConfirmation: authorUser?.email,
          startDate,
          welcomeText: bookWithAuthor?.welcome_text,
          // Project starts immediately if purchaser is author, so this is always false in the form.
          purchaserIsAuthor: false,
        })
      } else {
        setAuthorFormData({
          firstName: '',
          lastName: '',
          email: '',
          emailConfirmation: '',
          startDate,
          welcomeText: '',
          // Project starts immediately if purchaser is author, so this is always false in the form.
          purchaserIsAuthor: false,
        })
      }
    }
  }, [
    isAuthorQuerySuccess,
    bookWithAuthor,
    authorFormData,
    setAuthorFormData,
    purchaserIsntAuthor,
  ])

  // Init tags form state.
  useEffect(() => {
    // Initialize form one time from query result.
    if (existingTags?.length) {
      existingTags.forEach((tag) => {
        tagIdsSet.add(tag.id)
      })
    }
  }, [existingTags, tagIdsSet])

  const submitForm = () => {
    const { authorUser } = bookWithAuthor
    const promises = []
    promises.push(
      updateBook(bookId, projectInfoFormData).then((res) => {
        if (res.ok) {
          setProjectInfoUpdateComplete(true)
        }
      }),
    )

    if (!book.welcome_sent) {
      // TODO: Standardize case.
      let mainAuthorData = {
        lang,
        purchaser_first_name: role?.first_name,
        purchaser_last_name: role?.last_name,
        purchaser_email: role?.email,
        order_name: book?.order?.name,
        book_id: book?.id,
        purchaserIsAuthor: authorFormData.purchaserIsAuthor,
      }

      if (authorFormData.purchaserIsAuthor) {
        mainAuthorData = {
          ...mainAuthorData,
          authorfirstname: authorUser?.first_name,
          authorlastname: authorUser?.last_name,
          authoremail: authorUser?.email,
          authoremailverify: authorUser?.email,
          modalStartDate: bookWithAuthor.start_datetime,
          description: bookWithAuthor.welcome_text,
        }
      } else {
        mainAuthorData = {
          ...mainAuthorData,
          authorfirstname: authorFormData.firstName,
          authorlastname: authorFormData.lastName,
          authoremail: authorFormData.email,
          authoremailverify: authorFormData.emailConfirmation,
          modalStartDate: authorFormData.startDate.utc(),
          description: authorFormData.welcomeText,
        }
      }

      promises.push(
        axiosClient.post(`/purchase/mainAuthor`, mainAuthorData).then((res) => {
          if (res.ok) {
            setAuthorUpdateComplete(true)
          }
        }),
      )
    } else {
      setAuthorUpdateComplete(true)
    }

    // Save button processing == add people
    if (invites.length) {
      let postData = {
        invites,
        book_id: bookId,
        link: 'BOOK',
        bookSetupGuide: 'bookSetupGuide',
        invite_type: 'initial',
      }

      promises.push(
        axiosClient.post(`/book/${bookId}/sendInvite`, postData).then((res) => {
          if (res.ok) {
            setInvitesUpdateComplete(true)
          }
        }),
      )
    } else {
      setInvitesUpdateComplete(true)
    }

    const tagIds = Array.from(tagIdsSet)

    promises.push(
      axiosClient.post(`/book/${bookId}/tags`, { tagIds }).then((res) => {
        tagIdsSet.clear()
        if (res.ok) {
          setPromptsUpdateComplete(true)
        }
      }),
    )

    Promise.all(promises).then(() => {
      queryClient.invalidateQueries({ queryKey: ['book', bookId] })
      queryClient.invalidateQueries({ queryKey: ['bookAuthor'] })
    })

    nextStep()
  }

  // TODO: Lift buttons and style wrapper to this level.

  // Plan: Use router, pass each step onsubmit, next, and prev. Show buttons based on if passed. Store form states here.
  // MUST also think about custom hook to share form stuff between parent and child.
  if (!book.id) return <StyledLoader />

  return (
    <SetupGuideContainer>
      <div className="book-setup-guide">
        <div className="book-setup-guide-container">
          {step === 0 && <GuideIntro onNext={nextStep} />}
          {step > 0 && (
            <>
              <GuideHeader currentStep={step} />

              {/* TODO: Hide rather than selectively render to keep state? */}
              <div className="section-container">
                {step === 1 && (
                  <ProjectInfo
                    onNext={nextStep}
                    onPrevious={prevStep}
                    formData={projectInfoFormData}
                    setFormData={setProjectInfoFormData}
                  />
                )}

                {step === 2 && (
                  <AuthorDetails
                    formData={authorFormData}
                    currentAuthorData={currentAuthorData}
                    setFormData={setAuthorFormData}
                    onNext={nextStep}
                    onPrevious={prevStep}
                    isLoading={!isAuthorQuerySuccess}
                    isWelcomeSent={book.welcome_sent}
                  />
                )}

                {step === 3 && (
                  <AddPeople
                    onNext={nextStep}
                    onPrevious={prevStep}
                    invites={invites}
                    setInvites={setInvites}
                  />
                )}

                {step === 4 && (
                  <AddPrompts
                    onSave={submitForm}
                    onPrevious={prevStep}
                    selectedTagIds={selectedTagIds}
                    toggleTag={toggleTag}
                    isLoadingBookTags={!isBookTagsQuerySuccess}
                  />
                )}

                {step === 5 && (
                  <CompleteSetup
                    projectInfoUpdateComplete={projectInfoUpdateComplete}
                    authorUpdateComplete={authorUpdateComplete}
                    promptsUpdateComplete={promptsUpdateComplete}
                    invitesUpdateComplete={invitesUpdateComplete}
                    invitesCount={invites.length}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </SetupGuideContainer>
  )
}

export default SetupGuide
